import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  CircularProgress,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useDispatch, useSelector } from "react-redux";
import {
  getExpertises,
  createExpertise,
  updateExpertise,
  deleteExpertise,
} from "../../redux/expertisesSelice";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const ExpertiseManagement = () => {
  const dispatch = useDispatch();
  const { expertises, loading, error } = useSelector((state) => state.expertises);
  const expertiseList = Array.isArray(expertises?.data)
    ? expertises.data
    : Array.isArray(expertises)
    ? expertises
    : [];
  const [newExpertise, setNewExpertise] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editedExpertise, setEditedExpertise] = useState("");

  useEffect(() => {
    dispatch(getExpertises());
  }, [dispatch]);

  const handleAdd = () => {
    const trimmed = newExpertise.trim();
    if (!trimmed) return;

    const exists = expertiseList.some(
      (item) => item.name?.trim().toLowerCase() === trimmed.toLowerCase()
    );
    if (exists) {
      toast.error("Expertise already exists.");
      return;
    }

    dispatch(createExpertise({ name: newExpertise }))
      .unwrap()
      .then(() => {
        toast.success("Expertise added.");
        setNewExpertise("");
        dispatch(getExpertises());
      })
      .catch((err) =>
        toast.error(
          err?.response?.data?.message ||
            (typeof err === "string" ? err : err?.message) ||
            "Expertise creation failed."
        )
      );
  };

  const handleEditClick = (item) => {
    setEditingId(item._id);
    setEditedExpertise(item.name);
  };

  const handleUpdate = async () => {
    if (!editedExpertise.trim() || !editingId) return;
    console.log("Updating:", editingId, editedExpertise); // debug
    try {
      const result = await dispatch(
        updateExpertise({ id: editingId, data: { name: editedExpertise } })
      ).unwrap();
      console.log("Update result:", result); // debug
      toast.success("Expertise updated.");
      dispatch(getExpertises());
      setEditingId(null);
      setEditedExpertise("");
    } catch (error) {
      console.error("Update error:", error);
      toast.error(
        error?.response?.data?.message ||
          (typeof error === "string" ? error : error?.message) ||
          "Expertise update failed."
      );
    }
  };

  const handleDelete = (id) => {
    dispatch(deleteExpertise(id))
      .unwrap()
      .then(() => toast.success("Expertise deleted."))
      .catch((err) =>
        toast.error(
          err?.response?.data?.message ||
            (typeof err === "string" ? err : err?.message) ||
            "Expertise deletion failed."
        )
      );
  };

  useEffect(() => {
    if (error) {
      toast.error(
        typeof error === "string" ? error : "Expertise request failed."
      );
    }
  }, [error]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={5}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
        <Typography variant="h4" sx={{ fontSize: { xs: "20px", md: "24px" }, lineHeight: 1.3 }}>
          Expertise Management
        </Typography>
        <Box display="flex" gap={2} flexWrap="wrap">
          <TextField
            size="small"
            label="New expertise"
            value={newExpertise}
            onChange={(e) => setNewExpertise(e.target.value)}
          />
          <Button
            variant="contained"
            onClick={handleAdd}
            disabled={loading}
          >
            {loading ? "Adding..." : "Add"}
          </Button>
        </Box>
      </Box>

      <Box boxShadow={3} borderRadius={2} p={3}>
        <Typography variant="h6" gutterBottom>Current Expertises</Typography>
        {expertiseList.length === 0 ? (
          <Typography color="textSecondary">No expertise added yet.</Typography>
        ) : (
          <List>
            {expertiseList.map((item) => (
              <ListItem key={item._id} divider>
                {editingId === item._id ? (
                  <Box display="flex" alignItems="center" gap={1} sx={{ width: "100%" }}>
                    <TextField
                      value={editedExpertise}
                      onChange={(e) => setEditedExpertise(e.target.value)}
                      size="small"
                      fullWidth
                      sx={{ minWidth: 0, flex: "1 1 auto", maxWidth: "60%" }}
                    />
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      onClick={handleUpdate}
                      disabled={!editedExpertise.trim() || loading}
                    >
                      Save
                    </Button>
                    <Button
                      variant="outlined"
                      color="secondary"
                      size="small"
                      onClick={() => {
                        setEditingId(null);
                        setEditedExpertise("");
                      }}
                    >
                      Cancel
                    </Button>
                  </Box>
                ) : (
                  <ListItemText primary={item.name} />
                )}
                <ListItemSecondaryAction>
                  <IconButton
                    edge="end"
                    onClick={() => handleEditClick(item)}
                    disabled={loading}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    edge="end"
                    onClick={() => handleDelete(item._id)}
                    disabled={loading}
                  >
                    <DeleteIcon />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        )}
      </Box>
      <ToastContainer position="top-right" autoClose={3000} />
    </Box>
  );
};

export default ExpertiseManagement;
